
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import LIST_DIRECTORY from "@/apollo/directory/ListDirectorySimple.gql";
import { Contractor_Bool_Exp } from "@/generated/graphql";
import debounce from "lodash-es/debounce";

@Component
export default class Autocomplete extends Vue {
  @Prop({ type: String, default: "" }) readonly placeholder!: string;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;

  query: string = "";
  suggestions: any[] = [];
  noResultFound: boolean = false;
  showSuggestions: boolean = false;

  parseSearchQuery(query) {
    const tokens = query.match(/(?:[^\s"]+|"[^"]*")+/g) || [];
    const terms = [];
    let currentOperator = "AND";

    tokens.forEach((token, index) => {
      const upperToken = token.toUpperCase();
      if (["AND", "OR", "NOT"].includes(upperToken)) {
        currentOperator = upperToken;
      } else {
        terms.push({ term: token, operator: currentOperator });
        currentOperator = "AND"; // reset to default operator
      }
    });

    return terms;
  }

  handleInput2 = () => {
    // debounce(this.handleDebounce, 500)();
  };

  async handleInput() {
    const querySearch = this.query.trim().toLowerCase();
    if (querySearch !== "") {
      try {
        const { data } = await this.$apollo.query({
          query: LIST_DIRECTORY,
          variables: this.directoryFilter(querySearch),
        });
        this.suggestions = data.contractor.flatMap((contractor: any) => {
          const matches = [];
          if (contractor.user.given_name?.toLowerCase().includes(querySearch)) {
            matches.push({
              matchField: "givenname",
              matchValue: `${contractor.user.given_name}`,
              userId: contractor.user.id,
            });
          }
          if (
            contractor.user.family_name?.toLowerCase().includes(querySearch)
          ) {
            matches.push({
              matchField: "familyname",
              matchValue: `${contractor.user.family_name}`,
              userId: contractor.user.id,
            });
          }
          if (
            contractor.user.profile.job_title
              ?.toLowerCase()
              .includes(querySearch)
          ) {
            matches.push({
              matchField: "job_title",
              matchValue: contractor.user.profile.job_title,
              userId: contractor.user.id,
            });
          }
          if (contractor.tag?.name?.toLowerCase().includes(querySearch)) {
            matches.push({
              matchField: "tag",
              matchValue: contractor.tag?.name,
              userId: contractor.user.id,
            });
          }
          contractor.user.experiences?.forEach((exp: any) => {
            if (exp.job_title?.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "experience",
                matchValue: `${exp.job_title}`,
                userId: contractor.user.id,
              });
            } else if (exp.company_name?.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "experience",
                matchValue: `${exp.company_name}`,
                userId: contractor.user.id,
              });
            }
          });
          contractor.user.educations?.forEach((edu: any) => {
            if (edu?.institution?.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "education",
                matchValue: edu.institution,
                userId: contractor.user.id,
              });
            } else if (edu?.title?.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "education",
                matchValue: edu.title,
                userId: contractor.user.id,
              });
            }
          });
          contractor.user.profile?.skills?.forEach((skill: any) => {
            if (skill.skill?.name.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "skill",
                matchValue: skill.skill.name,
                userId: contractor.user.id,
              });
            }
          });
          contractor.user.profile?.languages?.forEach((lang: any) => {
            if (lang.language.name.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "language",
                matchValue: lang.language.name,
                userId: contractor.user.id,
              });
            }
          });
          if (
            contractor.user.profile?.country
              ?.toLowerCase()
              .includes(querySearch)
          ) {
            matches.push({
              matchField: "country",
              matchValue: contractor.user.profile.country,
              userId: contractor.user.id,
            });
          }
          if (
            contractor.user.user_profile_by_managements.some((management) =>
              management.country?.toLowerCase().includes(querySearch)
            )
          ) {
            matches.push({
              matchField: "country",
              matchValue: contractor.user.user_profile_by_managements
                .map((management) => management.country)
                .find((country) =>
                  country?.toLowerCase().includes(querySearch)
                ),
              userId: contractor.user.id,
            });
          }
          if (
            contractor.user.user_profile_by_managements.some((management) =>
              management.city?.toLowerCase().includes(querySearch)
            )
          ) {
            matches.push({
              matchField: "city",
              matchValue: contractor.user.user_profile_by_managements
                .map((management) => management.city)
                .find((city) => city?.toLowerCase().includes(querySearch)),
              userId: contractor.user.id,
            });
          }
          if (
            contractor.user.profile?.city?.toLowerCase().includes(querySearch)
          ) {
            matches.push({
              matchField: "city",
              matchValue: contractor.user.profile.city,
              userId: contractor.user.id,
            });
          }
          if (
            contractor.user.profile?.state?.toLowerCase().includes(querySearch)
          ) {
            matches.push({
              matchField: "region",
              matchValue: contractor.user.profile.state,
              userId: contractor.user.id,
            });
          }

          if (
            contractor.user.user_profile_by_managements.some((management) =>
              management.state?.toLowerCase().includes(querySearch)
            )
          ) {
            matches.push({
              matchField: "region",
              matchValue: contractor.user.user_profile_by_managements
                .map((management) => management.state)
                .find((state) => state?.toLowerCase().includes(querySearch)),
              userId: contractor.user.id,
            });
          }

          const profileMinRate = contractor.user.profile?.min_rate;
          const profileMaxRate = contractor.user.profile?.max_rate;
          const managementMinRate = contractor.user.user_profile_by_managements
            .map((management) => management.min_rate)
            .find((rate) => rate !== null && rate.toString() === querySearch);
          const managementMaxRate = contractor.user.user_profile_by_managements
            .map((management) => management.max_rate)
            .find((rate) => rate !== null && rate.toString() === querySearch);
          if (
            profileMinRate?.toString() === querySearch ||
            managementMinRate !== undefined
          ) {
            matches.push({
              matchField: "min rate",
              matchValue: `${profileMinRate || managementMinRate}`,
              userId: contractor.user.id,
            });
          }
          if (
            profileMaxRate?.toString() === querySearch ||
            managementMaxRate !== undefined
          ) {
            matches.push({
              matchField: "max rate",
              matchValue: `${profileMaxRate || managementMaxRate}`,
              userId: contractor.user.id,
            });
          }
          contractor.user.profile.industries?.forEach((ind: any) => {
            if (ind.industry?.name?.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "industries",
                matchValue: ind.industry.name,
                userId: contractor.user.id,
              });
            }
          });
          contractor.user.certifications?.forEach((cert: any) => {
            if (cert.title?.toLowerCase().includes(querySearch)) {
              matches.push({
                matchField: "certification",
                matchValue: cert.title,
                userId: contractor.user.id,
              });
            }
          });
          return matches;
        });
        this.showSuggestions = this.suggestions.length > 0;
        this.noResultFound = this.suggestions.length === 0;
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    } else {
      this.$emit("clear");
      this.showSuggestions = false;
      this.suggestions = [];
    }
  }

  @Watch("query")
  onQueryChange(newQuery: string) {
    if (newQuery.trim() === "") {
      this.showSuggestions = false;
      this.suggestions = [];
      this.noResultFound = false;
      this.$emit("clear");
    }
  }

  get uniqueSuggestions() {
    const uniqueMap = new Map();
    if (this.suggestions.length > 0) {
      this.suggestions.forEach((suggestion) => {
        const identifier = `${suggestion.matchField}-${suggestion.matchValue}`;
        if (!uniqueMap.has(identifier)) {
          uniqueMap.set(identifier, {
            ...suggestion,
            userIds: [suggestion.userId],
          });
        } else {
          uniqueMap.get(identifier).userIds.push(suggestion.userId);
        }
      });
      return Array.from(uniqueMap.values());
    }
  }

  hideSuggestions() {
    setTimeout(() => {
      this.showSuggestions = false;
    }, 200);
  }

  selectSuggestion(suggestion: any) {
    this.$emit("select", {
      matchField: suggestion.matchField,
      matchValue: suggestion.matchValue,
      userId: suggestion.userIds,
    });
    this.query = `${suggestion.matchValue}`;
    this.showSuggestions = false;
  }

  directoryFilter(searchName: string) {
    const filterConditions: Contractor_Bool_Exp[] = [
      {
        approved: { _eq: true },
        deleted: { _eq: false },
        _or: [
          {
            user: {
              archived: {
                _is_null: true,
              },
            },
          },
          {
            user: {
              archived: {
                _eq: false,
              },
            },
          },
        ],
      },
    ];

    if (searchName) {
      const nameConditions: any[] = [
        { profile: { job_title: { _ilike: `%${searchName}%` } } },
        {
          user_profile_by_managements: {
            job_title: { _ilike: `%${searchName}%` },
          },
        },

        {
          user_profile_by_managements: {
            state: { _ilike: `%${searchName}%` },
          },
        },
        { given_name: { _ilike: `%${searchName}%` } },
        { family_name: { _ilike: `%${searchName}%` } },
        { experiences: { job_title: { _ilike: `%${searchName}%` } } },
        { profile: { country: { _ilike: `%${searchName}%` } } },
        { profile: { state: { _ilike: `%${searchName}%` } } },
        { profile: { city: { _ilike: `%${searchName}%` } } },
        {
          user_profile_by_managements: {
            country: { _ilike: `%${searchName}%` },
          },
        },
        {
          profile: {
            skills: { skill: { name: { _ilike: `%${searchName}%` } } },
          },
        },
        {
          user_profile_by_managements: {
            city: { _ilike: `%${searchName}%` },
          },
        },
        {
          user_profile_by_managements: {
            country: { _ilike: `%${searchName}%` },
          },
        },
        { experiences: { company_name: { _ilike: `%${searchName}%` } } },
        { certifications: { title: { _ilike: `%${searchName}%` } } },
        { certifications: { institution: { _ilike: `%${searchName}%` } } },
        { educations: { institution: { _ilike: `%${searchName}%` } } },
        { educations: { title: { _ilike: `%${searchName}%` } } },
        {
          profile: {
            languages: {
              language: { name: { _ilike: `%${searchName}%` } },
            },
          },
        },
        {
          profile: {
            industries: {
              industry: { name: { _ilike: `%${searchName}%` } },
            },
          },
        },
      ];
      const numericRegex = /^-?\d+(\.\d*)?$/;
      if (numericRegex.test(searchName)) {
        const numericName = searchName;
        nameConditions.push(
          { profile: { min_rate: { _eq: numericName } } },
          { profile: { max_rate: { _eq: numericName } } },
          { user_profile_by_managements: { min_rate: { _eq: numericName } } },
          { user_profile_by_managements: { max_rate: { _eq: numericName } } }
        );
      }
      filterConditions.push({
        _or: [
          {
            tag: {
              name: { _ilike: `%${searchName}%` },
            },
          },
          {
            user: {
              _or: nameConditions,
            },
          },
        ],
      });
    }
    return {
      offset: 0,
      limit: 50,
      where: {
        _and: filterConditions,
      },
      id: this.currentUserId,
    };
  }
}
